import { config } from '../../config.ts';
import { verifyTypedData } from 'ethers';

export interface Eip712Signer {
  getAddress(): Promise<string>;
  signTypedData(domain: object, types: Record<string, Array<{ name: string; type: string }>>, value: object): Promise<string>;
}

const REGISTRATION_TYPES = {
  Registration: [
    { name: 'brokerId', type: 'string' },
    { name: 'chainId', type: 'uint256' },
    { name: 'timestamp', type: 'uint64' },
    { name: 'registrationNonce', type: 'uint256' },
  ],
};

const ADD_KEY_TYPES = {
  AddOrderlyKey: [
    { name: 'brokerId', type: 'string' },
    { name: 'chainId', type: 'uint256' },
    { name: 'orderlyKey', type: 'string' },
    { name: 'scope', type: 'string' },
    { name: 'timestamp', type: 'uint64' },
    { name: 'expiration', type: 'uint64' },
  ],
};

const SETTLE_PNL_TYPES = {
  SettlePnl: [
    { name: 'brokerId', type: 'string' },
    { name: 'chainId', type: 'uint256' },
    { name: 'settleNonce', type: 'uint64' },
    { name: 'timestamp', type: 'uint64' },
  ],
};

const WITHDRAW_TYPES = {
  Withdraw: [
    { name: 'brokerId', type: 'string' },
    { name: 'chainId', type: 'uint256' },
    { name: 'receiver', type: 'address' },
    { name: 'token', type: 'string' },
    { name: 'amount', type: 'uint256' },
    { name: 'withdrawNonce', type: 'uint64' },
    { name: 'timestamp', type: 'uint64' },
  ],
};

type AddOrderlyKeyData = { brokerId: string; chainId: number; orderlyKey: string; scope: string; timestamp: number; expiration: number; chainType?: 'EVM' | 'SOL'; tag?: string };

function makeOffChainDomain(chainId: number) {
  return { ...config.offChainDomain, chainId };
}

export function getOnChainVerifyingContract(): string {
  return config.onChainDomain.verifyingContract;
}

function makeOnChainDomain(chainId: number) {
  return { ...config.onChainDomain, chainId };
}

function normalizeEvmSignature(signature: string): string {
  if (!/^0x[0-9a-fA-F]{130}$/.test(signature)) return signature;

  const v = Number.parseInt(signature.slice(-2), 16);
  if (v !== 0 && v !== 1) return signature;

  return `${signature.slice(0, -2)}${(v + 27).toString(16)}`;
}

function getAddOrderlyKeyTypedValue(data: AddOrderlyKeyData) {
  const { brokerId, chainId, orderlyKey, scope, timestamp, expiration } = data;
  return {
    brokerId,
    chainId,
    orderlyKey,
    scope,
    timestamp,
    expiration,
  };
}

export async function signRegistration(
  signer: Eip712Signer,
  data: { brokerId: string; chainId: number; timestamp: number; registrationNonce: string },
): Promise<string> {
  return normalizeEvmSignature(await signer.signTypedData(makeOffChainDomain(data.chainId), REGISTRATION_TYPES, data));
}

export async function signAddOrderlyKey(
  signer: Eip712Signer,
  data: AddOrderlyKeyData,
): Promise<string> {
  return normalizeEvmSignature(
    await signer.signTypedData(makeOffChainDomain(data.chainId), ADD_KEY_TYPES, getAddOrderlyKeyTypedValue(data)),
  );
}

export function recoverAddOrderlyKeySigner(data: AddOrderlyKeyData, signature: string): string {
  return verifyTypedData(makeOffChainDomain(data.chainId), ADD_KEY_TYPES, getAddOrderlyKeyTypedValue(data), signature);
}

export async function signSettlePnl(
  signer: Eip712Signer,
  data: { brokerId: string; chainId: number; settleNonce: number; timestamp: number },
): Promise<string> {
  return normalizeEvmSignature(await signer.signTypedData(makeOnChainDomain(data.chainId), SETTLE_PNL_TYPES, data));
}

export async function signWithdraw(
  signer: Eip712Signer,
  data: { brokerId: string; chainId: number; receiver: string; token: string; amount: bigint; withdrawNonce: number; timestamp: number },
): Promise<string> {
  return normalizeEvmSignature(await signer.signTypedData(makeOnChainDomain(data.chainId), WITHDRAW_TYPES, data));
}
